// The base no-unused-vars rule does not see type-only uses.
// eslint-disable-next-line no-unused-vars
import { Locale, localeLabel } from '../../../utils/locales';
import { describeError as describeApiError, entryMessage } from '../../../utils/serverErrors';
// eslint-disable-next-line no-unused-vars
import { ApiError } from '../../../utils/serverErrors';
import { arcKey } from '../../../utils/arcRows';
import { convertDateTimePretty } from '../../../utils/ReadableConverter';
// eslint-disable-next-line no-unused-vars
import { LanguagePair } from '../../../utils/arcRows';
// eslint-disable-next-line no-unused-vars
import * as baseRates from '../BaseRates/baseRates';

export interface GeneratedRate {
  languagePair: LanguagePair;
  price: { amount: number; currency: string };
  per?: string;
}

export interface Draft {
  priceSchemeId: string;
  name: string;
  description?: string | null;
  service?: number | null;
  media?: number | null;
  subject?: number | null;
  rush?: number | null;
  excludedArcs?: LanguagePair[] | null;
  baseRateVersion?: number;
  basePrices?: GeneratedRate[] | null;
}

/** Pricing's change-set types, which arrive lower case. */
export type ChangeSetType = 'create' | 'update';

/** Pricing's change-set statuses, which arrive lower case. */
export type ChangeSetStatus = 'pending' | 'approved' | 'rejected' | 'stale' | 'failed';

/** What a change set and its entry in a model's history share. */
export interface ChangeSetBase {
  changeSetId: string;
  type: ChangeSetType;
  status: ChangeSetStatus;
  author: string;
  createdAt: string;
  // Null for a create.
  baseVersion: number | null;
  inputVersions: Record<string, number>;
  // Only while a base-rate or TER-group change set is failed: how many of all its live models its
  // approvals have made live so far.
  appliedModels?: number;
  totalModels?: number;
}

/** A change set as a model's history lists it, with its review. */
export interface ChangeSetSummary extends ChangeSetBase {
  // The reviewer's user id and the ISO-8601 time of the approve or reject; null until reviewed.
  reviewedBy: string | null;
  reviewedAt: string | null;
  // Null unless a reject gave one.
  reviewComment: string | null;
}

export interface ChangeSet extends ChangeSetBase {
  organizationId: string;
  entityType: string;
  entityId: string;
  entityName: string;
  priceSchemeId: string;
  draft: Draft;
}

// The live fields are null until a change set is approved.
export interface PricingModelSummary {
  priceSchemeId: string;
  name: string;
  description?: string | null;
  author: string;
  createdAt: string;
  version: number | null;
  liveChangeSetId: string | null;
  liveApprovedAt: string | null;
  liveApprovedBy: string | null;
  // Null when the model has no change set.
  latestChangeSetId: string | null;
  latestChangeSetAt: string | null;
  latestChangeSetAuthor: string | null;
  pendingChangeSetCount: number;
}

export interface PricingModel extends PricingModelSummary {
  latestChangeSet: ChangeSet | null;
  // The change set the live version was approved from; null until one is approved.
  liveChangeSet: ChangeSet | null;
  // Newest first.
  changeSets: ChangeSetSummary[];
}

// Router location state carried from a create to the created model's page.
export interface SubmittedState {
  submittedChangeSetId: string;
}

/** A model's "Latest change": when and by whom, or '-' when the model has no change set. */
export const latestChangeText = (at: string | null, author: string | null): string => {
  if (!at) {
    return '-';
  }
  return author ? `${convertDateTimePretty(at)} by ${author}` : convertDateTimePretty(at);
};

/** The history entry of a change set just saved, which is pending and so not yet reviewed. */
export const summaryOf = ({
  changeSetId,
  type,
  status,
  author,
  createdAt,
  baseVersion,
  inputVersions,
}: ChangeSetBase): ChangeSetSummary => ({
  changeSetId,
  type,
  status,
  author,
  createdAt,
  baseVersion,
  inputVersions,
  reviewedBy: null,
  reviewedAt: null,
  reviewComment: null,
});

/**
 * The change set an edit starts from: the live one when the model has one, so an edit begins from
 * what is in use rather than from a newer draft still awaiting review; otherwise the latest.
 */
export const findChangeSetToEdit = <C>(model: {
  liveChangeSet: C | null;
  latestChangeSet: C | null;
}): C | null => model.liveChangeSet || model.latestChangeSet;

export type Multiplier = 'service' | 'media' | 'subject' | 'rush';

export const MULTIPLIERS: { field: Multiplier; label: string }[] = [
  { field: 'service', label: 'Service multiplier' },
  { field: 'media', label: 'Media multiplier' },
  { field: 'subject', label: 'Subject multiplier' },
  { field: 'rush', label: 'Rush multiplier' },
];

const DEFAULT_MULTIPLIER = '1.0';

export interface FormValues {
  name: string;
  description: string;
  service: string;
  media: string;
  subject: string;
  rush: string;
  // Arc keys, as built by arcKey.
  excludedArcs: string[];
}

export type FormField = keyof FormValues;

export type FieldErrors = Partial<Record<FormField, string>>;

// The name and description are set on create and cannot change, so an update leaves them out.
export interface PricingModelUpdate {
  service: number;
  media: number;
  subject: number;
  rush: number;
  excludedArcs: LanguagePair[];
}

export interface PricingModelRequest extends PricingModelUpdate {
  name: string;
  description: string;
}

export const emptyForm = (): FormValues => ({
  name: '',
  description: '',
  service: DEFAULT_MULTIPLIER,
  media: DEFAULT_MULTIPLIER,
  subject: DEFAULT_MULTIPLIER,
  rush: DEFAULT_MULTIPLIER,
  excludedArcs: [],
});

const multiplierText = (value: number | null | undefined): string =>
  value === null || value === undefined ? DEFAULT_MULTIPLIER : String(value);

const formFromDraft = (draft: Draft): FormValues => ({
  name: draft.name,
  description: draft.description || '',
  service: multiplierText(draft.service),
  media: multiplierText(draft.media),
  subject: multiplierText(draft.subject),
  rush: multiplierText(draft.rush),
  excludedArcs: (draft.excludedArcs || []).map(arcKey),
});

/**
 * The form for editing a model, from the draft `findChangeSetToEdit` picks. The name, and the
 * description when the model has one, are the model's own, which cannot change after create.
 */
export const formFromModel = (model: PricingModel): FormValues => {
  const changeSet = findChangeSetToEdit(model);
  const draft = changeSet && changeSet.draft;
  const form = draft ? formFromDraft(draft) : emptyForm();
  return { ...form, name: model.name, description: model.description || form.description };
};

export const parseArc = (key: string): LanguagePair => {
  const [sourceLocale, targetLocale] = JSON.parse(key);
  return { sourceLocale, targetLocale };
};

export const toUpdateRequest = (values: FormValues): PricingModelUpdate => ({
  service: Number(values.service),
  media: Number(values.media),
  subject: Number(values.subject),
  rush: Number(values.rush),
  excludedArcs: values.excludedArcs.map(parseArc),
});

export const toCreateRequest = (values: FormValues): PricingModelRequest => ({
  name: values.name.trim(),
  description: values.description.trim(),
  ...toUpdateRequest(values),
});

const DECIMAL_PATTERN = /^\d+(\.\d+)?$/;
const MAX_DECIMALS = 3;

export const DESCRIPTION_REQUIRED_MESSAGE = 'Enter a description.';

export const validateMultiplier = (value: string): string | undefined => {
  const trimmed = value.trim();
  const multiplier = Number(trimmed);
  if (trimmed === '' || !Number.isFinite(multiplier)) {
    return 'Enter a number.';
  }
  if (multiplier <= 0 || multiplier >= 2) {
    return 'Enter a value greater than 0 and less than 2.';
  }
  if (!DECIMAL_PATTERN.test(trimmed)) {
    return 'Enter a number.';
  }
  const fraction = trimmed.split('.')[1] || '';
  if (fraction.length > MAX_DECIMALS) {
    return `Use at most ${MAX_DECIMALS} decimal places.`;
  }
  return undefined;
};

/** Checks the multipliers and, on a create, that the name and description are entered. */
export const validateForm = (values: FormValues, creating: boolean): FieldErrors => {
  const errors: FieldErrors = {};
  if (creating && values.name.trim() === '') {
    errors.name = 'Enter a name.';
  }
  if (creating && values.description.trim() === '') {
    errors.description = DESCRIPTION_REQUIRED_MESSAGE;
  }
  MULTIPLIERS.forEach(({ field }) => {
    const error = validateMultiplier(values[field]);
    if (error) {
      errors[field] = error;
    }
  });
  return errors;
};

export const hasFieldErrors = (errors: FieldErrors): boolean => Object.values(errors).some(Boolean);

export const arcLabel = (arc: LanguagePair, locales: Locale[] | null): string => {
  const localeByCode = new Map((locales || []).map(locale => [locale.code, locale]));
  const label = (code: string | null): string =>
    localeLabel(code, code === null ? undefined : localeByCode.get(code));
  return `${label(arc.sourceLocale)} → ${label(arc.targetLocale)}`;
};

export interface ArcOption {
  label: string;
  value: string;
}

/**
 * One option per base-rate arc, plus any already-excluded arc the base rates no longer have so
 * that saving does not silently drop it.
 */
export const arcOptions = (
  basePrices: baseRates.BasePrice[],
  excludedArcs: string[],
  locales: Locale[] | null
): ArcOption[] => {
  const keys = basePrices.map(basePrice => arcKey(basePrice.languagePair));
  const known = new Set(keys);
  return [...keys, ...excludedArcs.filter(key => !known.has(key))].map(key => ({
    label: arcLabel(parseArc(key), locales),
    value: key,
  }));
};

const FIELDS: FormField[] = [
  'name',
  'description',
  'service',
  'media',
  'subject',
  'rush',
  'excludedArcs',
];

const ACCESS_DENIED_MESSAGE = 'You do not have permission to manage pricing models.';
const REJECTED_MESSAGE = 'Pricing rejected the pricing model.';
const INVALID_VALUE_MESSAGE = 'Invalid value';

export const describeError = (error: ApiError): string[] =>
  describeApiError(error, ACCESS_DENIED_MESSAGE);

export interface SaveFailure {
  fieldErrors: FieldErrors;
  formErrors: string[];
}

export interface ModelSaveErrorOptions<F extends string> {
  fields: readonly F[];
  // An error on a field the form does not show is shown with the form's errors.
  shown: (field: F) => boolean;
  // The submitted excluded arcs, which an excludedArcs error path indexes.
  excludedArcs: LanguagePair[];
  locales: Locale[] | null;
  rejectedMessage: string;
  accessDeniedMessage: string;
}

/**
 * Puts each validation error of a model's save on the field its path names; an excluded-arc error
 * names the arc at that index of the submitted request. Used by the pricing and scoping model
 * pages.
 */
export const describeModelSaveError = <F extends string>(
  error: ApiError,
  options: ModelSaveErrorOptions<F>
): { fieldErrors: Partial<Record<F, string>>; formErrors: string[] } => {
  if (error.status !== 422) {
    return { fieldErrors: {}, formErrors: describeApiError(error, options.accessDeniedMessage) };
  }
  const fieldErrors: Partial<Record<F, string>> = {};
  const formErrors: string[] = [];
  const entries = (error.details && error.details.errors) || [];
  entries.forEach(entry => {
    const message = entryMessage(entry);
    const [segment, index] = entry.path || [];
    const field = options.fields.find(name => name === segment);
    if (!field || !options.shown(field)) {
      formErrors.push(message || options.rejectedMessage);
      return;
    }
    let fieldMessage = message || INVALID_VALUE_MESSAGE;
    const arc = typeof index === 'number' ? options.excludedArcs[index] : undefined;
    if (field === 'excludedArcs' && arc) {
      fieldMessage = `${arcLabel(arc, options.locales)}: ${fieldMessage}`;
    }
    const existing = fieldErrors[field];
    fieldErrors[field] = existing ? `${existing} ${fieldMessage}` : fieldMessage;
  });
  if (formErrors.length === 0 && !Object.values(fieldErrors).some(Boolean)) {
    formErrors.push(options.rejectedMessage);
  }
  return { fieldErrors, formErrors };
};

export const describeSaveError = (
  error: ApiError,
  request: PricingModelUpdate,
  locales: Locale[] | null
): SaveFailure =>
  describeModelSaveError(error, {
    fields: FIELDS,
    shown: () => true,
    excludedArcs: request.excludedArcs,
    locales,
    rejectedMessage: REJECTED_MESSAGE,
    accessDeniedMessage: ACCESS_DENIED_MESSAGE,
  });

/** A change-set type or status as a label, e.g. 'pending' as 'Pending'. */
export const formatLabel = (value: ChangeSetType | ChangeSetStatus): string =>
  value.charAt(0).toUpperCase() + value.slice(1);
